import React, { useState, useEffect } from 'react'
import {
  Button,
  Col,
  Drawer,
  Input,
  Row,
  PageHeader,
  Table,
  Switch,
  message
} from 'antd'
import debounce from 'lodash/debounce'
import VariantsAddOptionModal from './VariantsAddOptionModal'
import ProductVariantForm from './ProductVariantForm'
import { useMutation, useApolloClient } from '@apollo/client'
import {
  UPDATE_PRODUCT_VARIANTS,
  UPDATE_PRODUCT_VARIANT_SKU
} from '../ProductQueries'
import { ProductInfo, ProductVariant } from '@slerp/controls'
import { PlusCircleOutlined } from '@ant-design/icons'
import { EditOutlined, LoadingOutlined } from '@ant-design/icons'
import { StoreId } from '@slerp/controls'

interface VariantsFormProps {
  product: ProductInfo
  storeId: Array<StoreId>
  refetchProducts: () => void
}

const VariantsForm = (props: VariantsFormProps) => {
  const { product, storeId, refetchProducts } = props
  const [productVariant, setProductVariant] = useState([...product.children])
  const [loadingProductVariant, setLoadingProductVariant] = useState('')
  const [rowEditProcessing, setRowEditProcessing] = useState(false)
  const [rowUpdating, setRowUpdating] = useState(false)

  const [show, setShow] = useState(false)
  const [showEditForm, setShowEditForm] = useState(false)
  const client = useApolloClient()
  const options: object[] = []
  const [record, setRecord] = useState<ProductVariant>({
    archived_at: '',
    default_variant: false,
    height: 0,
    image: '',
    inserted_at: '',
    key: '',
    length: 0,
    limit: 0,
    name: '',
    options: options,
    price: 0,
    published: false,
    quantity: 0,
    restrictions: {
      id: '',
      alcoholic: false
    },
    sku: '',
    vat: 0,
    weight: 0,
    width: 0
  })
  const [updateProductVariants] = useMutation(UPDATE_PRODUCT_VARIANTS)
  const [updateProductVariantSku] = useMutation(UPDATE_PRODUCT_VARIANT_SKU)

  useEffect(() => {
    setProductVariant([...product.children])
  }, [product])

  const handleSwitch = (id: String, target: string, value: boolean) => {
    const targetVariant = productVariant.find((variant) => variant.key === id)!
    const values = {
      id: targetVariant.key,
      published: target === 'published' ? value : targetVariant.published,
      sku: targetVariant.sku,
      price: targetVariant.price,
      vat: targetVariant.vat,
      limit: targetVariant.limit,
      restrictions: {
        id: targetVariant.restrictions.id,
        alcoholic:
          target === 'restrictions'
            ? value
            : targetVariant.restrictions.alcoholic
      }
    }

    updateProductVariants({
      variables: values
    })
      .then((result) => {
        message.destroy()
        client.resetStore()
        message.success('Updated!', 1)
      })
      .catch(() => {
        message.destroy()
        client.resetStore()
        message.error('Update Failed!', 1)
      })
  }

  const updateProductSku = (sku: string, id: string) => {
    setRowUpdating(true)
    updateProductVariantSku({
      variables: { id, sku }
    })
      .then((response) => {
        setRowEditProcessing(false)
        setRowUpdating(false)
        setLoadingProductVariant('')
        refetchProducts()
      })
      .catch(() => {
        setRowEditProcessing(false)
        setRowUpdating(false)
        setLoadingProductVariant('')
        refetchProducts()
        message.destroy()
        message.error('SKU update failed', 1)
      })
  }

  const debouncedUpdateProductSku = debounce(updateProductSku, 1000)

  const columns = [
    {
      key: 'action',
      render: (value: ProductVariant, record: ProductVariant) =>
        !record.default_variant && (
          <EditOutlined
            onClick={() => {
              setShowEditForm(true)
              setRecord(record)
            }}
          />
        )
    },
    {
      title: 'Published',
      dataIndex: 'published',
      key: 'published',
      render: (value: boolean, record: ProductVariant) => (
        <Switch
          checked={value}
          onChange={(value) => handleSwitch(record.key, 'published', value)}
        />
      )
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name'
    },
    {
      title: 'SKU',
      dataIndex: 'sku',
      key: 'sku',
      render: (value: string, record: ProductVariant) => {
        const isCurrentRowUpdating = loadingProductVariant === record.key
        return (
          <Row align='middle'>
            <Col>
              <Input
                defaultValue={value}
                onChange={(e) => {
                  setRowEditProcessing(true)
                  setLoadingProductVariant(record.key)
                  debouncedUpdateProductSku(e.target.value, record.key)
                }}
                disabled={!isCurrentRowUpdating && rowEditProcessing}
              />
            </Col>
            <Col offset={1}>
              {rowUpdating && isCurrentRowUpdating && <LoadingOutlined />}
            </Col>
          </Row>
        )
      }
    },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price'
    },
    {
      title: 'VAT',
      dataIndex: 'vat',
      key: 'vat'
    },
    {
      title: 'Order Limit',
      dataIndex: 'limit',
      key: 'limit'
    },
    {
      title: 'Age Verification',
      dataIndex: 'restrictions',
      key: 'restrictions',
      render: (
        value: { alcoholic: boolean; id: string },
        record: ProductVariant
      ) => (
        <Switch
          checked={value.alcoholic}
          onChange={(value) => handleSwitch(record.key, 'restrictions', value)}
        />
      )
    }
  ]

  const onProductVariantDrawerClose = () => setShowEditForm(false)

  return (
    <>
      <Row>
        <PageHeader title='Product Variants' style={{ paddingBottom: '0' }} />
      </Row>
      <Row style={{ marginBottom: '20px' }}>
        <label>
          Add options (for example, size or colour) to the table to create more
          variants
        </label>
      </Row>
      <Row style={{ marginBottom: '24px' }}>
        <Button
          data-testid='add-option'
          size='large'
          type='text'
          onClick={() => setShow(!show)}
        >
          <PlusCircleOutlined
            style={{ fontSize: '20px', marginRight: '10px' }}
          />
          <label style={{ color: '#4d4954' }}>Add variant option</label>
        </Button>
      </Row>
      <VariantsAddOptionModal
        show={show}
        setShow={setShow}
        productVariants={product.children}
        product={product}
        storeId={storeId}
      />
      <Drawer
        width={720}
        onClose={onProductVariantDrawerClose}
        visible={showEditForm}
        title={`${record?.name}`}
      >
        <ProductVariantForm
          setShow={setShowEditForm}
          record={record}
          refetchProducts={refetchProducts}
        />
      </Drawer>
      <Row>
        <Table
          dataSource={productVariant}
          pagination={false}
          columns={columns}
          style={{ width: '100%' }}
        />
      </Row>
    </>
  )
}

export default VariantsForm
